import { LoadFunctionArguments } from "@tellescope/sdk";
import { getPropertyFromVar } from "./get-property-from-var";

type SortGetSomeRequest<T> = Pick<LoadFunctionArguments<T>, "sort" | "sortBy">;

/**
 * Maps a sortBy field name to the actual object property name used in the model.
 * "timestamp" is an alias for "createdAt" in the SDK sort API.
 * "dueDateInMS" corresponds to the "dueDate" property on the model.
 */
const sortByToPropertyName = (sortBy: LoadFunctionArguments<unknown>["sortBy"]): string => {
  switch (sortBy) {
    case "timestamp":
      return "createdAt";
    case "dueDateInMS":
      return "dueDate";
    case "closedAt":
    case "updatedAt":
      return sortBy;
    default:
      return "createdAt";
  }
};

/**
 * Gets the numeric timestamp from a value that may be a Date object or a date string.
 * Returns undefined if the value cannot be converted to a valid time.
 */
const toTimeValue = (value: unknown): number | undefined => {
  if (value instanceof Date) return value.getTime();
  if (typeof value === "string") {
    const t = new Date(value).getTime();
    return isNaN(t) ? undefined : t;
  }
  if (typeof value === "number") return value;
  return undefined;
};

/**
 * Returns a comparator function for use with Array.sort / upsertInInfiniteDataList's sortFn,
 * derived from a getSome query request's `sort` and `sortBy` fields.
 *
 * Default behavior (no sort/sortBy specified) matches the API default: createdAt descending (newest first).
 *
 * @example
 * const comparator = getSortComparator({ sort: "oldFirst", sortBy: "updatedAt" });
 * upsertInInfiniteDataList({ ..., sortFn: comparator });
 */
export const getSortComparator = <T>({ sort, sortBy }: SortGetSomeRequest<T>): ((a: T, b: T) => number) => {
  const ascending = sort === "oldFirst";
  const propertyName = sortByToPropertyName(sortBy);

  return (a: T, b: T): number => {
    if (sortBy === "dueDateInMS") {
      // Numeric comparison for dueDate
      const aVal = getPropertyFromVar<number>(a, propertyName);
      const bVal = getPropertyFromVar<number>(b, propertyName);

      if (aVal === undefined && bVal === undefined) return 0;
      if (aVal === undefined) return ascending ? -1 : 1;
      if (bVal === undefined) return ascending ? 1 : -1;

      return ascending ? aVal - bVal : bVal - aVal;
    }

    // Date comparison (handles both Date objects and ISO strings)
    const aTime = toTimeValue(getPropertyFromVar(a, propertyName));
    const bTime = toTimeValue(getPropertyFromVar(b, propertyName));

    if (aTime === undefined && bTime === undefined) return 0;
    if (aTime === undefined) return ascending ? -1 : 1;
    if (bTime === undefined) return ascending ? 1 : -1;

    return ascending ? aTime - bTime : bTime - aTime;
  };
};

type SortListBySortByFilterArgs<
  T,
  SortBy extends LoadFunctionArguments<T>["sortBy"],
  Sort extends LoadFunctionArguments<T>["sort"],
> = {
  list: T[];
  sorting: {
    sortBy: SortBy;
    sort: Sort;
  };
};

/**
 * Helper function to sort by date strings (ISO format)
 */
const sortByDateString = <T>(list: T[], propertyName: string, ascending: boolean): T[] => {
  return [...list].sort((a, b) => {
    const aValue = getPropertyFromVar<string>(a, propertyName);
    const bValue = getPropertyFromVar<string>(b, propertyName);

    if (!aValue && !bValue) return 0;
    if (!aValue) return ascending ? 1 : -1;
    if (!bValue) return ascending ? -1 : 1;

    const aTime = new Date(aValue).getTime();
    const bTime = new Date(bValue).getTime();

    return ascending ? aTime - bTime : bTime - aTime;
  });
};

/**
 * Helper function to sort by numeric values
 */
const sortByNumber = <T>(list: T[], propertyName: string, ascending: boolean): T[] => {
  return [...list].sort((a, b) => {
    const aValue = getPropertyFromVar<number>(a, propertyName);
    const bValue = getPropertyFromVar<number>(b, propertyName);

    if (aValue === undefined && bValue === undefined) return 0;
    if (aValue === undefined) return ascending ? 1 : -1;
    if (bValue === undefined) return ascending ? -1 : 1;

    return ascending ? aValue - bValue : bValue - aValue;
  });
};

export const sortListBySortByFilter = <
  T,
  SortBy extends LoadFunctionArguments<T>["sortBy"],
  Sort extends LoadFunctionArguments<T>["sort"],
>({
  list,
  sorting: { sortBy, sort },
}: SortListBySortByFilterArgs<T, SortBy, Sort>): T[] => {
  const ascending = sort === "oldFirst";

  if (!list.length) return list;

  const listItem = list[0];

  if (typeof listItem !== "object" || listItem === null) return list;

  switch (sortBy) {
    case "timestamp":
      if (!("createdAt" in listItem)) {
        return list;
      }

      return sortByDateString(list, "createdAt", ascending);
    case "closedAt":
      if (!("closedAt" in listItem)) {
        return list;
      }

      return sortByDateString(list, "closedAt", ascending);
    case "dueDateInMS":
      if (!("dueDate" in listItem)) {
        return list;
      }

      return sortByNumber(list, "dueDate", ascending);
    case "updatedAt":
      if (!("updatedAt" in listItem)) {
        return list;
      }

      return sortByDateString(list, "updatedAt", ascending);
    default:
      return list;
  }
};
